/**
 * Runtime configuration.
 *
 * Values come from `window.__APP_CONFIG__` (injected by `/config.js` at page
 * load, written by the container entrypoint from environment variables).
 * Falls back to build-time `import.meta.env.VITE_*` so local dev (where
 * Vite reads a `.env` file) still works without the script being in place.
 *
 * Adding a new setting:
 *   1. Add a field below.
 *   2. Add it to `public/config.js` placeholder (empty string).
 *   3. Add it to `entrypoint.sh` so the container renders it from env.
 */

type RawConfig = Record<string, string | boolean | number | undefined>;

declare global {
  interface Window {
    __APP_CONFIG__?: RawConfig;
  }
}

const runtime: RawConfig =
  (typeof window !== 'undefined' && window.__APP_CONFIG__) || {};

// Values are always strings (possibly empty). Callers coerce to boolean/number
// the same way they did when reading from `import.meta.env` directly —
// preserves existing `=== 'true'`, `Number(...)`, and truthiness patterns.
function read(key: string): string {
  const r = runtime[key];
  if (typeof r === 'string' && r !== '') return r;
  const b = (import.meta.env as Record<string, unknown>)[key];
  return typeof b === 'string' ? b : '';
}

export const config = {
  VITE_BE_ENDPOINT: read('VITE_BE_ENDPOINT'),
  VITE_BE_ADMIN_PREFIX: read('VITE_BE_ADMIN_PREFIX'),
  VITE_SOCKET_ENDPOINT: read('VITE_SOCKET_ENDPOINT'),
  VITE_STRIPE_PUBLISHABLE_KEY: read('VITE_STRIPE_PUBLISHABLE_KEY'),
  VITE_RESOURCE_CENTER_URL: read('VITE_RESOURCE_CENTER_URL'),
  VITE_CONSULTANT_RESOURCE_CENTER_URL: read(
    'VITE_CONSULTANT_RESOURCE_CENTER_URL',
  ),
  VITE_CONSULTANT_PROMO_BANNER_LINK: read('VITE_CONSULTANT_PROMO_BANNER_LINK'),
  VITE_FRANCHISOR_TCX_SCOUT_LINK: read('VITE_FRANCHISOR_TCX_SCOUT_LINK'),
  VITE_CONSULTANT_TCX_SCOUT_LINK: read('VITE_CONSULTANT_TCX_SCOUT_LINK'),
  VITE_OUTLET_BY_YEAR_TABLE_NUMBER: read('VITE_OUTLET_BY_YEAR_TABLE_NUMBER'),
  VITE_STATUS_OF_FRANCHISED_OUTLETS_TABLE_NUMBER: read(
    'VITE_STATUS_OF_FRANCHISED_OUTLETS_TABLE_NUMBER',
  ),
  VITE_ACTIVE_BETA_FORM: read('VITE_ACTIVE_BETA_FORM'),
  VITE_FRANCHABLE_V2: read('VITE_FRANCHABLE_V2'),
  VITE_SKIP_SUBSCRIPTION: read('VITE_SKIP_SUBSCRIPTION'),
  VITE_ENABLE_EZEE_ASSIST: read('VITE_ENABLE_EZEE_ASSIST'),
  VITE_SITE_MAINTENANCE: read('VITE_SITE_MAINTENANCE'),
  // Base URL for FCH-owned public assets (default avatar, profile images):
  // `<endpoint>/<bucket>/app/fch-dev`. One config value so a bucket move is a
  // config change, not edits across components (TCX-718).
  VITE_ASSET_BASE_URL: read('VITE_ASSET_BASE_URL'),
  // Base URL for shared marketing media on the CDN Space (tutorial videos).
  VITE_MEDIA_CDN_URL: read('VITE_MEDIA_CDN_URL'),
} as const;

export type AppConfig = typeof config;
